import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Plus,
    Check,
    Target,
    Calendar,
    ChevronRight,
    X,
    Trash2,
} from 'lucide-react';
import { useAppState, Goal } from '../../contexts/AppContext';

const GOAL_COLORS = [
    '#0D5F6B', '#2A7A85', '#EF4444', '#F59E0B',
    '#10B981', '#8B5CF6', '#EC4899', '#3B82F6',
];

const GOAL_ICONS = ['🎯', '💼', '💪', '📚', '💰', '❤️', '🏠', '✈️', '🎨', '🌟'];

const GOAL_CATEGORIES: { id: Goal['category']; label: string; icon: string }[] = [
    { id: 'career', label: 'Career', icon: '💼' },
    { id: 'health', label: 'Health', icon: '💪' },
    { id: 'personal', label: 'Personal', icon: '🌟' },
    { id: 'financial', label: 'Financial', icon: '💰' },
    { id: 'education', label: 'Education', icon: '📚' },
    { id: 'relationships', label: 'Relationships', icon: '❤️' },
    { id: 'other', label: 'Other', icon: '🎯' },
];

export default function AppGoals() {
    const navigate = useNavigate();
    const { goals, addGoal, toggleMilestone, deleteGoal } = useAppState();

    const [showAddModal, setShowAddModal] = useState(false);
    const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);
    const [newGoalTitle, setNewGoalTitle] = useState('');
    const [newGoalDescription, setNewGoalDescription] = useState('');
    const [newGoalIcon, setNewGoalIcon] = useState('🎯');
    const [newGoalColor, setNewGoalColor] = useState(GOAL_COLORS[0]);
    const [newGoalCategory, setNewGoalCategory] = useState<Goal['category']>('personal');
    const [newGoalTargetDate, setNewGoalTargetDate] = useState('');
    const [newMilestones, setNewMilestones] = useState<string[]>([]);
    const [milestoneInput, setMilestoneInput] = useState('');

    const handleAddGoal = () => {
        if (!newGoalTitle.trim()) return;

        addGoal({
            title: newGoalTitle.trim(),
            description: newGoalDescription.trim() || undefined,
            icon: newGoalIcon,
            color: newGoalColor,
            category: newGoalCategory,
            targetDate: newGoalTargetDate || undefined,
            milestones: newMilestones,
        });

        // Reset form
        setNewGoalTitle('');
        setNewGoalDescription('');
        setNewGoalIcon('🎯');
        setNewGoalColor(GOAL_COLORS[0]);
        setNewGoalCategory('personal');
        setNewGoalTargetDate('');
        setNewMilestones([]);
        setShowAddModal(false);
    };

    const handleAddMilestone = () => {
        if (!milestoneInput.trim()) return;
        setNewMilestones(prev => [...prev, milestoneInput.trim()]);
        setMilestoneInput('');
    };

    const removeMilestone = (index: number) => {
        setNewMilestones(prev => prev.filter((_, i) => i !== index));
    };

    const activeGoals = goals.filter(g => g.status === 'active');
    const completedGoals = goals.filter(g => g.status === 'completed');

    const getDaysRemaining = (targetDate: string) => {
        const target = new Date(targetDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffTime = target.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] text-white px-4 pt-6 pb-8">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Target className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Goals</h1>
                        <p className="text-white/80 text-sm">
                            {activeGoals.length} active • {completedGoals.length} completed
                        </p>
                    </div>
                </div>

                {/* Overall Progress */}
                {activeGoals.length > 0 && (
                    <div className="bg-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/80">Average Progress</span>
                            <span className="font-bold">
                                {Math.round(activeGoals.reduce((sum, g) => sum + g.progress, 0) / activeGoals.length)}%
                            </span>
                        </div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-white rounded-full transition-all duration-300"
                                style={{
                                    width: `${activeGoals.reduce((sum, g) => sum + g.progress, 0) / activeGoals.length}%`
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Goals List */}
            <div className="p-4 -mt-4 space-y-3">
                {goals.length === 0 ? (
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="w-16 h-16 bg-[#D9EEF2] rounded-full flex items-center justify-center mx-auto mb-4">
                            <Target className="w-8 h-8 text-[#0D5F6B]" />
                        </div>
                        <h3 className="font-semibold text-[#1A2832] dark:text-white mb-2">No goals yet</h3>
                        <p className="text-sm text-[#7B8C9B] mb-4">
                            Set meaningful goals and track your progress
                        </p>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="px-4 py-2 bg-[#0D5F6B] text-white rounded-xl font-medium"
                        >
                            Add First Goal
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Active Goals */}
                        {activeGoals.map(goal => (
                            <div
                                key={goal.id}
                                className="bg-white dark:bg-dark-card rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08)] overflow-hidden"
                            >
                                <div
                                    className="p-4 cursor-pointer"
                                    onClick={() => setExpandedGoalId(
                                        expandedGoalId === goal.id ? null : goal.id
                                    )}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                                            style={{ backgroundColor: `${goal.color}20` }}
                                        >
                                            {goal.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-[#1A2832] dark:text-white truncate">
                                                {goal.title}
                                            </h3>
                                            <div className="flex items-center gap-2 text-sm text-[#7B8C9B]">
                                                <span className="capitalize">{goal.category}</span>
                                                {goal.targetDate && (
                                                    <>
                                                        <span>•</span>
                                                        <Calendar className="w-3 h-3" />
                                                        <span>
                                                            {getDaysRemaining(goal.targetDate) > 0
                                                                ? `${getDaysRemaining(goal.targetDate)} days left`
                                                                : 'Overdue'
                                                            }
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="text-sm font-semibold"
                                                style={{ color: goal.color }}
                                            >
                                                {goal.progress}%
                                            </span>
                                            <ChevronRight
                                                className={`w-5 h-5 text-[#7B8C9B] transition-transform ${expandedGoalId === goal.id ? 'rotate-90' : ''
                                                    }`}
                                            />
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-300"
                                            style={{
                                                width: `${goal.progress}%`,
                                                backgroundColor: goal.color,
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Expanded Milestones */}
                                {expandedGoalId === goal.id && (
                                    <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                                        {goal.description && (
                                            <p className="text-sm text-[#7B8C9B] mb-3">
                                                {goal.description}
                                            </p>
                                        )}

                                        {goal.milestones.length > 0 ? (
                                            <div className="space-y-2">
                                                <h4 className="text-xs font-medium text-[#7B8C9B] uppercase tracking-wide">
                                                    Milestones
                                                </h4>
                                                {goal.milestones.map(milestone => (
                                                    <button
                                                        key={milestone.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleMilestone(goal.id, milestone.id);
                                                        }}
                                                        className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-gray-50"
                                                    >
                                                        <div
                                                            className={`w-5 h-5 rounded-full flex items-center justify-center ${milestone.completed
                                                                ? ''
                                                                : 'border-2 border-gray-300'
                                                                }`}
                                                            style={milestone.completed ? { backgroundColor: goal.color } : {}}
                                                        >
                                                            {milestone.completed && (
                                                                <Check className="w-3 h-3 text-white" />
                                                            )}
                                                        </div>
                                                        <span className={`text-sm ${milestone.completed
                                                            ? 'text-[#7B8C9B] line-through'
                                                            : 'text-[#1A2832]'
                                                            }`}>
                                                            {milestone.title}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-[#7B8C9B]">
                                                No milestones added
                                            </p>
                                        )}

                                        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (confirm('Delete this goal?')) {
                                                        deleteGoal(goal.id);
                                                    }
                                                }}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Completed Goals */}
                        {completedGoals.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-sm font-medium text-[#7B8C9B] mb-3 px-1">
                                    Completed ({completedGoals.length})
                                </h3>
                                {completedGoals.map(goal => (
                                    <div
                                        key={goal.id}
                                        className="bg-white/60 rounded-2xl p-4 mb-2 opacity-75"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                                                style={{ backgroundColor: `${goal.color}20` }}
                                            >
                                                {goal.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-[#1A2832] dark:text-white line-through">
                                                    {goal.title}
                                                </h3>
                                            </div>
                                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Add Goal Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end z-50">
                    <div className="bg-white dark:bg-dark-card w-full rounded-t-3xl p-6 max-h-[85vh] overflow-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-[#1A2832]">New Goal</h2>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        {/* Goal Title */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Goal Title
                            </label>
                            <input
                                type="text"
                                value={newGoalTitle}
                                onChange={(e) => setNewGoalTitle(e.target.value)}
                                placeholder="e.g., Get promoted to senior engineer"
                                className="w-full px-4 py-3 border border-[#DCE4EA] rounded-xl outline-none focus:border-[#0D5F6B]"
                            />
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Description (optional)
                            </label>
                            <textarea
                                value={newGoalDescription}
                                onChange={(e) => setNewGoalDescription(e.target.value)}
                                placeholder="Why is this goal important to you?"
                                rows={2}
                                className="w-full px-4 py-3 border border-[#DCE4EA] rounded-xl outline-none focus:border-[#0D5F6B] resize-none"
                            />
                        </div>

                        {/* Category */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Category
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {GOAL_CATEGORIES.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setNewGoalCategory(cat.id)}
                                        className={`px-3 py-2 rounded-xl flex items-center gap-2 ${newGoalCategory === cat.id
                                            ? 'bg-[#0D5F6B] text-white'
                                            : 'bg-gray-100 text-[#4A5C6A]'
                                            }`}
                                    >
                                        <span>{cat.icon}</span>
                                        <span className="text-sm">{cat.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Icon Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Icon
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {GOAL_ICONS.map(icon => (
                                    <button
                                        key={icon}
                                        onClick={() => setNewGoalIcon(icon)}
                                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${newGoalIcon === icon
                                            ? 'bg-[#0D5F6B] ring-2 ring-offset-2 ring-[#0D5F6B]'
                                            : 'bg-gray-100'
                                            }`}
                                    >
                                        {icon}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Color
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {GOAL_COLORS.map(color => (
                                    <button
                                        key={color}
                                        onClick={() => setNewGoalColor(color)}
                                        className={`w-8 h-8 rounded-full ${newGoalColor === color
                                            ? 'ring-2 ring-offset-2 ring-gray-400'
                                            : ''
                                            }`}
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Target Date */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Target Date (optional)
                            </label>
                            <input
                                type="date"
                                value={newGoalTargetDate}
                                onChange={(e) => setNewGoalTargetDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-3 border border-[#DCE4EA] rounded-xl outline-none focus:border-[#0D5F6B]"
                            />
                        </div>

                        {/* Milestones */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Milestones (optional)
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={milestoneInput}
                                    onChange={(e) => setMilestoneInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone()}
                                    placeholder="Add a milestone..."
                                    className="flex-1 px-4 py-3 border border-[#DCE4EA] rounded-xl outline-none focus:border-[#0D5F6B]"
                                />
                                <button
                                    onClick={handleAddMilestone}
                                    className="px-4 py-3 bg-[#0D5F6B] text-white rounded-xl"
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                            </div>
                            {newMilestones.length > 0 && (
                                <div className="space-y-2">
                                    {newMilestones.map((m, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                                        >
                                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                                            <span className="flex-1 text-sm">{m}</span>
                                            <button
                                                onClick={() => removeMilestone(i)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={handleAddGoal}
                            disabled={!newGoalTitle.trim()}
                            className="w-full py-4 bg-[#0D5F6B] text-white rounded-xl font-semibold disabled:opacity-50"
                        >
                            Create Goal
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
